import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

const WebPFormatGuide = () => {

  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("webp-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        {/* Header Section */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #6f42c1 0%, #d63384 100%)",
            color: "#fff",
          }}
        >
          <h1 className="display-5 fw-bold mb-3 position-relative">
            WebP: The Modern Image Format That's Changing the Web
          </h1>
          <p className="lead mb-0 position-relative fs-4">
            Smaller files, better quality, and why you should care
          </p>
          <div className="mt-4 position-relative">
            <span style={{ color: '#6f42c1' }} className="badge bg-light text-purple fs-6 me-2 p-2">
              Next-Gen Format
            </span>
            <span style={{ color: '#6f42c1' }} className="badge bg-light text-purple fs-6 me-2 p-2">
              Web Performance
            </span>
            <span style={{ color: '#6f42c1' }} className="badge bg-light text-purple fs-6 p-2">
              Modern Web
            </span>
          </div>
        </header>

        {/* Content Section */}
        <section className="p-4 p-md-5">
          {/* Introduction */}
          <div
            className="alert alert-purple border-0 rounded-3 shadow-sm"
            style={{ backgroundColor: '#6f42c1', color: 'white' }}
            role="alert"
          >
            <div className="d-flex">
              <div className="me-3 fs-4">🚀</div>
              <div>
                <p className="fs-5 lh-lg mb-0">
                  I've seen websites cut their image load times in half just by switching to WebP. 
                  This isn't some distant future technology - it's here, it's supported, and it's 
                  dramatically better than older formats. Let me show you why WebP is worth the switch.
                </p>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="alert alert-info border-0 rounded-3 mb-5">
            <div className="d-flex">
              <div className="me-3 fs-5">💡</div>
              <div>
                <strong>Browser Support Update:</strong> WebP now works in all modern browsers including 
                Chrome (23+), Firefox (65+), Edge (18+), and Safari (14+). For older browsers, 
                you'll need fallbacks - but the coverage is now excellent for most users.
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header text-white py-3" style={{ backgroundColor: '#6f42c1' }}>
              <h2 className="h4 mb-0 fw-bold">What we're covering</h2>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "What makes WebP special",
                      "The compression magic", 
                      "Where WebP dominates",
                      "Limitations you should know",
                      "Real-world performance",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 1}`}
                          className="text-decoration-none text-purple hover-text-dark"
                          style={{ color: '#6f42c1' }}
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Implementation guide",
                      "Tools & conversion",
                      "Browser support strategy",
                      "When to use WebP",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 6}`}
                          className="text-decoration-none text-purple hover-text-dark"
                          style={{ color: '#6f42c1' }}
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div id="section-1" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                1
              </span>
              Why WebP is the format we've been waiting for
            </h2>
            <p className="text-secondary fs-5">
              WebP is Google's modern image format that combines the best features of JPEG, PNG, and GIF 
              into one versatile format. It's like having a Swiss Army knife for web images.
            </p>
            <div className="row g-3 mt-3">
              {[
                {
                  icon: "📉",
                  text: "Superior Compression",
                  desc: "25-35% smaller than JPEG at same quality, 26% smaller than PNG"
                },
                {
                  icon: "🎨",
                  text: "Lossless & Lossy",
                  desc: "Both compression modes in one format"
                },
                {
                  icon: "💎",
                  text: "Alpha Transparency",
                  desc: "PNG-like transparency with better compression"
                },
                {
                  icon: "🎬",
                  text: "Animation Support",
                  desc: "GIF-like animation with smaller file sizes"
                },
                {
                  icon: "🌐",
                  text: "Modern Support",
                  desc: "Works in all major modern browsers"
                },
                {
                  icon: "⚡",
                  text: "Faster Loading",
                  desc: "Smaller files mean faster page loads"
                },
              ].map((item, index) => (
                <div key={index} className="col-6 col-md-4 col-lg-2">
                  <div className="p-3 border rounded-3 text-center bg-light hover-lift transition-all h-100">
                    <div className="fs-2 mb-2">{item.icon}</div>
                    <div className="small fw-medium text-dark">{item.text}</div>
                    <div className="text-muted small mt-1">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2 */}
          <div id="section-2" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                2
              </span>
              The compression magic: How WebP beats the classics
            </h2>
            <div className="row g-4">
              {[
                {
                  icon: "🔮",
                  title: "Predictive Coding",
                  desc: "Uses sophisticated prediction to encode images more efficiently than JPEG's block-based approach"
                },
                {
                  icon: "🎯",
                  title: "Adaptive Quantization",
                  desc: "Smartly allocates bits where they matter most for visual quality"
                },
                {
                  icon: "🔄",
                  title: "VP8 Video Codec",
                  desc: "Built on same technology as video compression - extremely efficient"
                },
                {
                  icon: "🌈",
                  title: "Better Color Handling",
                  desc: "Superior color space conversion and chroma subsampling"
                },
              ].map((item, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3 bg-light h-100">
                    <span className="fs-2 me-3">{item.icon}</span>
                    <div>
                      <h5 className="fw-bold mb-1">{item.title}</h5>
                      <p className="text-secondary mb-0">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="alert alert-info mt-4" role="alert">
              <strong>Real comparison:</strong> A typical website hero image that's 300KB as JPEG can be around 200KB as WebP 
              with the same visual quality. That's 100KB saved per image - which adds up fast across a page.
            </div>
          </div>

          {/* Section 3 - Where WebP Dominates */}
          <div id="section-3" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                3
              </span>
              Where WebP absolutely dominates the competition
            </h2>
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm">
                <thead style={{ backgroundColor: '#6f42c1', color: 'white' }}>
                  <tr>
                    <th className="border-0">Use Case</th>
                    <th className="border-0">WebP Advantage</th>
                    <th className="border-0">Traditional Format</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      useCase: "Website Photos",
                      advantage: "25-35% smaller than JPEG, same quality",
                      traditional: "JPEG - larger files"
                    },
                    {
                      useCase: "Logos with Transparency",
                      advantage: "26% smaller than PNG, alpha channel support",
                      traditional: "PNG - larger files"
                    },
                    {
                      useCase: "Simple Animations",
                      advantage: "Much smaller than GIF, better quality",
                      traditional: "GIF - limited colors, larger files"
                    },
                    {
                      useCase: "E-commerce Images",
                      advantage: "Faster load times, better UX",
                      traditional: "JPEG/PNG mix - slower loading"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{row.useCase}</td>
                      <td className="text-success">{row.advantage}</td>
                      <td>
                        <code>{row.traditional}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 4 - Limitations */}
          <div id="section-4" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                4
              </span>
              Limitations you absolutely need to know
            </h2>
            
            <div className="row g-4">
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#dc3545' }}>
                    <h5 className="mb-0 fw-bold">🚫 Browser Support Gaps</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Internet Explorer</strong> - No support at all</li>
                      <li><strong>Old Safari</strong> - Pre-iOS 14, pre-macOS Big Sur</li>
                      <li><strong>Legacy mobile browsers</strong> - Older Android browsers</li>
                      <li><strong>Some enterprise environments</strong> - Locked-down older browsers</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-danger me-2">Fallbacks Needed</span>
                      <span className="badge bg-danger">Check Analytics</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#fd7e14' }}>
                    <h5 className="mb-0 fw-bold">⚡ Technical Limitations</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>No progressive rendering</strong> - Unlike JPEG's progressive scan</li>
                      <li><strong>Slower encoding</strong> - 5-10x slower compression than JPEG</li>
                      <li><strong>Limited editing software</strong> - Not all apps support WebP editing</li>
                      <li><strong>No CMYK support</strong> - RGB color space only</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-warning me-2">Workflow Impact</span>
                      <span className="badge bg-warning">Encoding Time</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 - Real World Performance */}
          <div id="section-5" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                5
              </span>
              Real-world performance: What you actually gain
            </h2>
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#6f42c1' }}>
                    <h5 className="mb-0 fw-bold">📈 E-commerce Site</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>40 product images</strong> per page</li>
                      <li><strong>JPEG: 12MB total</strong></li>
                      <li><strong>WebP: 8MB total</strong></li>
                      <li><strong>33% smaller</strong> - 4MB savings</li>
                      <li>Load time: 3.2s → 2.1s</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#6f42c1' }}>
                    <h5 className="mb-0 fw-bold">📱 News Website</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Article with 15 images</strong></li>
                      <li><strong>Mixed JPEG/PNG: 4.5MB</strong></li>
                      <li><strong>WebP: 3.2MB</strong></li>
                      <li><strong>29% smaller</strong> - 1.3MB savings</li>
                      <li>Load time: 2.8s → 1.9s</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#6f42c1' }}>
                    <h5 className="mb-0 fw-bold">🎨 Portfolio Site</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>High-quality gallery</strong></li>
                      <li><strong>PNG: 8MB for logos+UI</strong></li>
                      <li><strong>WebP: 5.9MB</strong></li>
                      <li><strong>26% smaller</strong> - 2.1MB savings</li>
                      <li>Load time: 4.1s → 2.7s</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 - Implementation Guide */}
          <div id="section-6" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                6
              </span>
              Implementation guide: How to actually use WebP
            </h2>
            <div className="row g-3">
              {[
                "Use <picture> element with WebP first, traditional formats as fallback",
                "Convert your existing images in batches - don't do it manually",
                "Set up automated conversion in your build process or CMS",
                "Test fallbacks thoroughly - especially on older devices",
                "Use quality setting 75-85 for WebP (WebP 80 ≈ JPEG 67 in visual quality)",
                "Monitor your analytics to see how many users get fallbacks",
              ].map((tip, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-center p-3 border rounded-3 bg-light h-100">
                    <div
                      className="text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "30px", height: "30px", backgroundColor: '#6f42c1' }}
                    >
                      {index + 1}
                    </div>
                    <span className="fw-medium">{tip}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-4 bg-light rounded-3">
              <h5 className="fw-bold mb-3">Example HTML Implementation:</h5>
              <pre className="bg-dark text-light p-3 rounded">
                {`<picture>
  <source srcset="image.webp" type="image/webp">
  <source srcset="image.jpg" type="image/jpeg">
  <img src="image.jpg" alt="Description">
</picture>`}
              </pre>
            </div>
          </div>

          {/* Section 7 - Tools */}
          <div id="section-7" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                7
              </span>
              Essential WebP tools that actually work
            </h2>
            <div className="list-group shadow-sm">
              {[
                {
                  name: "Squoosh by Google",
                  desc: "The best WebP converter. Web-based, shows live comparisons, and gives you full control over compression settings.",
                  badge: "Recommended",
                  link: "https://squoosh.app"
                },
                {
                  name: "Imagemin WebP Plugin",
                  desc: "Node.js plugin for automated conversion in build processes. Perfect for CI/CD pipelines and static site generators.",
                  link: "https://github.com/imagemin/imagemin-webp"
                },
                {
                  name: "GIMP 2.10+",
                  desc: "Native built-in WebP support for import and export, including animation, ICC profiles, and metadata.",
                  link: "https://www.gimp.org"
                },
                {
                  name: "WebP Converter CLI",
                  desc: "Command-line tools for batch conversion. cwebp for conversion, dwebp for decoding back to other formats.",
                  link: "https://developers.google.com/speed/webp/docs/precompiled"
                },
              ].map((tool, index) => (
                <div
                  key={index}
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                >
                  <div>
                    <h6 className="mb-1 fw-bold">
                      <a
                        href={tool.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-dark"
                      >
                        {tool.name}
                      </a>
                    </h6>
                    <p className="mb-1 text-secondary">{tool.desc}</p>
                  </div>
                  {tool.badge && (
                    <span className="badge rounded-pill" style={{ backgroundColor: '#6f42c1' }}>
                      {tool.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Section 8 - Browser Support Strategy */}
          <div id="section-8" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                8
              </span>
              Browser support strategy: Covering all your users
            </h2>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#6f42c1' }}>
                    <h5 className="mb-0">✅ Modern Browsers (90%+ users)</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>Chrome 23+ (2012+) - Full support</li>
                      <li>Firefox 65+ (2019+)</li>
                      <li>Edge 18+ (2018+)</li>
                      <li>Safari 14+ (2020+)</li>
                      <li>Opera 12.1+ (2012+) - Full support</li>
                      <li>Mobile browsers (modern)</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#dc3545' }}>
                    <h5 className="mb-0">❌ Need Fallbacks (5-10% users)</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>Internet Explorer (all versions)</li>
                      <li>Safari 13 and older</li>
                      <li>Old Android browsers</li>
                      <li>Enterprise legacy browsers</li>
                      <li>Some mobile browsers</li>
                      <li>Specialized browsers</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="alert alert-info mt-4" role="alert">
              <strong>Check your analytics!</strong> The exact percentage of users needing fallbacks varies by your audience. 
              Business sites might have more legacy users than consumer tech sites.
            </div>
          </div>

          {/* Section 9 - When to Use WebP */}
          <div id="section-9" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                9
              </span>
              When you should (and shouldn't) use WebP
            </h2>
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#198754' }}>
                    <h5 className="mb-0 fw-bold">✅ Perfect For WebP</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Website photographs</strong> - replacing JPEG</li>
                      <li><strong>Logos with transparency</strong> - replacing PNG</li>
                      <li><strong>Simple animations</strong> - replacing GIF</li>
                      <li><strong>E-commerce product images</strong></li>
                      <li><strong>Social media images</strong></li>
                      <li><strong>Blog post images</strong></li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#fd7e14' }}>
                    <h5 className="mb-0 fw-bold">⚠️ Consider Alternatives</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Print design work</strong> - stick with TIFF/PSD</li>
                      <li><strong>Camera originals</strong> - keep RAW files</li>
                      <li><strong>Medical/scientific imaging</strong> - lossless formats</li>
                      <li><strong>Extremely wide compatibility</strong> needed</li>
                      <li><strong>Progressive loading required</strong></li>
                      <li><strong>Limited editing software access</strong></li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header text-white py-3" style={{ backgroundColor: '#6f42c1' }}>
                    <h5 className="mb-0 fw-bold">⚡ Future Formats</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>AVIF</strong> - 50% smaller than JPEG, 20-30% smaller than WebP</li>
                      <li><strong>JPEG XL</strong> - Better compression but limited browser support</li>
                      <li><strong>HEIC</strong> - Apple ecosystem</li>
                      <li><strong>WebP 2</strong> - Future improvements</li>
                    </ul>
                    <div className="mt-2 p-2 bg-light rounded">
                      <small className="text-muted">
                        <strong>Note:</strong> JPEG XL currently only has native support in Safari 17+. 
                        Chrome and Firefox do not support it as of 2024-2025.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 10 - FAQ */}
          {/* <div id="section-10" className="mb-5">
            <h2 className="h3 fw-bold mb-4 border-bottom pb-2" style={{ color: '#6f42c1' }}>
              <span
                className="text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px", backgroundColor: '#6f42c1' }}
              >
                10
              </span>
              Your WebP Questions Answered
            </h2>
            
            <div className="accordion" id="webpFAQ">
              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                    Is WebP really better than JPEG and PNG?
                  </button>
                </h3>
                <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#webpFAQ">
                  <div className="accordion-body">
                    <p><strong>For file size and performance:</strong> Yes, absolutely. WebP typically produces files 25-35% smaller than JPEG at the same quality level, and 26% smaller than PNG for lossless images with transparency.</p>
                    <p><strong>For compatibility:</strong> Not yet. You still need fallbacks for older browsers, though modern browser support is now excellent.</p>
                    <p><strong>For editing:</strong> Limited. Not all image editors support WebP natively yet.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                    What's the difference between WebP quality and JPEG quality settings?
                  </button>
                </h3>
                <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#webpFAQ">
                  <div className="accordion-body">
                    <p>The quality scales are different between WebP and JPEG. WebP quality 80 produces similar visual quality to JPEG quality 67, but with a significantly smaller file size.</p>
                    <p><strong>Practical advice:</strong> Use WebP quality 75-85 for most web images. This gives you excellent visual quality while maintaining the file size benefits that make WebP worthwhile.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                    How do I handle browsers that don't support WebP?
                  </button>
                </h3>
                <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#webpFAQ">
                  <div className="accordion-body">
                    <p>Use the HTML <code>&lt;picture&gt;</code> element with fallbacks:</p>
                    <pre className="bg-light p-2 rounded">{`<picture>
  <source srcset="image.webp" type="image/webp">
  <source srcset="image.jpg" type="image/jpeg">
  <img src="image.jpg" alt="Description">
</picture>`}</pre>
                    <p>Browsers that support WebP will download the WebP version, while older browsers will fall back to JPEG or PNG automatically.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                    Is WebP encoding really 5-10x slower than JPEG?
                  </button>
                </h3>
                <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#webpFAQ">
                  <div className="accordion-body">
                    <p>Yes, WebP encoding is significantly slower than JPEG encoding - typically 5-10x slower for comparable quality settings.</p>
                    <p><strong>However, decoding performance</strong> is much closer - only about 1.3-1.5x slower than JPEG. This means the user experience impact is minimal once the images are delivered.</p>
                    <p><strong>Solution:</strong> Convert images in batches during your build process rather than on-the-fly, so the performance hit happens during development, not for your users.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq5">
                    Should I use WebP or wait for AVIF/JPEG XL?
                  </button>
                </h3>
                <div id="faq5" className="accordion-collapse collapse" data-bs-parent="#webpFAQ">
                  <div className="accordion-body">
                    <p><strong>Use WebP now</strong> for immediate performance benefits. The browser support is excellent and the file size savings are substantial.</p>
                    <p><strong>AVIF</strong> offers even better compression but browser support is still growing (Chrome, Firefox, Opera support it).</p>
                    <p><strong>JPEG XL</strong> has great features but currently only Safari 17+ supports it natively.</p>
                    <p><strong>Best approach:</strong> Implement WebP now with fallbacks, and plan to add AVIF as a future enhancement when browser support improves.</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Final CTA */}
          <div
            className="text-center mt-5 p-5 rounded-4"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            }}
          >
            <h2 className="h1 fw-bold mb-3" style={{ color: '#6f42c1' }}>
              Ready to speed up your website with WebP?
            </h2>
            <p className="fs-5 text-secondary mb-4">
              Get our curated collection of WebP sample files and see the performance difference for yourself.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <a
                href="https://samplefiles.dev/sample-images/sample-webp"
                className="btn btn-lg px-5 py-3 shadow hover-lift text-white"
                style={{ backgroundColor: '#6f42c1' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                🚀 Get WebP Sample Files
              </a>
              <a
                href="#section-1"
                className="btn btn-outline-primary btn-lg px-5 py-3"
                style={{ borderColor: '#6f42c1', color: '#6f42c1' }}
              >
                📖 Read This Again
              </a>
            </div>
            <p className="mt-3 text-muted small">
              Various compression levels and types - perfect for testing and comparison.
            </p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default WebPFormatGuide;

// "Common questions answered",
